import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataListDefault, DataListResponse, WorkTab } from "types/ui/card-item";
import TabsLayout from "components/tabs-layout";
import BoardBackground from "components/board/board-bg";
import Table, { tableHeader } from "components/table";
import { IconDownload, IconFolderOpen } from "@tabler/icons-react";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import {
  PaymentListResponse,
  PaymentProps,
  PaymentFilters,
  PaymentFiltersDefault,
  PaymentTableResponse,
} from "types/payment";
import { useSharedState } from "state";
import { StatusEnum } from "types/dict";
import { exportPaymentsToExcel, getPayments, getPaymentsTable } from "api/payment";

export default function PaymentsPage() {
  const {
    payments,
    setPayments,
    paymentsTable,
    setPaymentsTable,
    currentPageTab,
    searchQuery,
    handleSearch,
    handleClearFilters,
    setOffset,
    firstEntry,
    filtersList,
    filtersCount,
    user,
    getPaymentsData,
    getPaymentsTableData,
    updateFirstEntry,
    notify,
  } = useSharedState();

  const navigate = useNavigate();
  const { locationName } = useParams();

  const [paymentCards, setPaymentCards] = useState<DataListResponse>(DataListDefault);
  const [localPayments, setLocalPayments] = useState<PaymentListResponse>(payments);
  const [localPaymentsTable, setLocalPaymentsTable] = useState(paymentsTable);
  const [paymentsLength, setPaymentsLength] = useState(0);

  const selectedLocation = user.locations.find((loc) => loc.location === locationName);

  useEffect(() => {
    setLocalPayments(payments);
  }, [payments]);

  useEffect(() => {
    setLocalPaymentsTable(paymentsTable);
  }, [paymentsTable]);

  useEffect(() => {
    setPaymentCards(convertPaymentsToCards(localPayments));
  }, [localPayments]);

  useEffect(() => {
    // When location changes or becomes null, fetch appropriate data
    const fetchData = async () => {
      try {
        if (locationName) {
          // Fetch filtered data for specific location
          const filters: PaymentFilters = {
            ...PaymentFiltersDefault,
            locations: [locationName],
            title: searchQuery || undefined,
          };

          if (currentPageTab === "cards") {
            const res: PaymentListResponse = await getPayments(filters);
            setPayments(res);
            setLocalPayments(res);
            setPaymentsLength(Object.values(res).reduce((a, b) => a + b.total_count, 0));
          } else if (currentPageTab === "table") {
            const tableFilters = { ...filters, offset: 0 };
            const res: PaymentTableResponse = await getPaymentsTable(tableFilters);
            setPaymentsTable(res);
            setLocalPaymentsTable(res);
          }
        } else {
          // Fetch unfiltered data when no location is selected
          if (currentPageTab === "cards") {
            await getPaymentsData();
          } else if (currentPageTab === "table") {
            await getPaymentsTableData(0);
          }
        }
        setOffset(0);
        updateFirstEntry(1);
      } catch (error: any) {
        console.error("Error fetching payments data:", error);
      }
    };

    fetchData();
  }, [locationName, currentPageTab]);

  const onPaymentPageChange = async (offset: number) => {
    setOffset(offset);
    updateFirstEntry(offset + 1);

    const filters: PaymentFilters = {
      ...PaymentFiltersDefault,
      locations: locationName ? [locationName] : undefined,
      title: searchQuery || undefined,
      offset,
    };

    try {
      const res: PaymentTableResponse = await getPaymentsTable(filters);
      setLocalPaymentsTable(res);
    } catch (error: any) {
      console.error("Error fetching paginated payments:", error);
    }
  };

  const handleExportPayment = async () => {
    try {
      const filters: PaymentFilters = {
        ...PaymentFiltersDefault,
        search: searchQuery || undefined,
        locations: locationName ? [locationName] : undefined,
        from_date: filtersList.from_date || undefined,
        to_date: filtersList.to_date || undefined,
        contragent_id: Number(filtersList.contragent_id) || undefined,
        template_name: filtersList.template_name || undefined,
        project: filtersList.project || undefined,
        creator_name: filtersList.creator_name || undefined,
        status: filtersList.status ? [filtersList.status] : undefined,
      };

      const res = await exportPaymentsToExcel(filters);

      // Create blob URL from the binary data
      const blob = new Blob([res]);
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      const fileName = `payments_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.xlsx`;

      link.href = blobUrl;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL to free memory
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при экспорте оплат");
    }
  };

  const formattedCards = (paymentsByStatus: PaymentProps[]) => {
    return paymentsByStatus?.map((payment) => ({
      id: payment.payment_request_id,
      title: (
        <>
          <div>{payment.choosen_location}</div>
          <div>{payment.department_name}</div>
          <div>{payment.contragent}</div>
          <div>{payment.project}</div>
        </>
      ),
      description: payment.opisanie_rasxoda,
      status: payment.status,
      dueDate: payment.request_date,
      created_by: payment.creator_name,
      assigned_to:
        payment.approver_name && payment.approver_name !== "unknown" ? payment.approver_name : payment.creator_name,
      priority: undefined,
    }));
  };

  const convertPaymentsToCards = (payments: PaymentListResponse): DataListResponse => {
    return {
      "0": {
        data: formattedCards(payments["0"]?.data),
        total_count: payments["0"]?.total_count,
      },
      "1": {
        data: formattedCards(payments["1"]?.data),
        total_count: payments["1"]?.total_count,
      },
      "4": {
        data: formattedCards(payments["4"]?.data),
        total_count: payments["4"]?.total_count,
      },
      "2": {
        data: formattedCards(payments["2"]?.data),
        total_count: payments["2"]?.total_count,
      },
    };
  };

  const getStatusTitle = (status: number) => {
    switch (status) {
      case StatusEnum.TO_DO:
        return "К выполнению";
      case StatusEnum.IN_PROGRESS:
        return "В работе";
      case StatusEnum.COMPLETED:
        return "Завершён";
      case StatusEnum.REJECTED:
        return "Отклонён";
      case StatusEnum.IN_EXECUTION:
        return "К закрытию";
      default:
        return "-";
    }
  };

  const paymentsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Наименование", narrow: false, wrap: false },
    { header_text: "Шаблон", narrow: false, wrap: false },
    { header_text: "Проект", narrow: false, wrap: false },
    { header_text: "Контрагент", narrow: false, wrap: false },
    { header_text: "Итоговая сумма", narrow: false, wrap: false },
    { header_text: "Создатель", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: true, wrap: false },
  ];

  const paymentsList = (
    <>
      {localPaymentsTable.data.map((payment, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(payment.title || "-")}</td>
          <td>{oneLine(payment.template_name || "-")}</td>
          <td>{oneLine(payment.project || "-")}</td>
          <td>{oneLine(payment.contragent || "-")}</td>
          <td>{oneLine(payment.total_sum.toLocaleString() || "-")}</td>
          <td>{oneLine(payment.creator_name || "-")}</td>
          <td>{oneLine(getStatusTitle(payment.status))}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconFolderOpen className="icon" />}
                title="Подробнее"
                action={() => navigate(`${payment.payment_request_id}`)}
                color={"primary"}
                modal={false}
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const paymentsTableComponent = Table(paymentsTableHeaders, paymentsList, true, {
    total: localPaymentsTable.total_count,
    updateInfo: onPaymentPageChange,
  });

  const payment_tabs: WorkTab[] = [
    {
      id: "cards",
      title: "Заявки по статусам",
      component: <BoardBackground data={paymentCards} />,
    },
    { id: "table", title: "Таблица", component: paymentsTableComponent },
  ];

  const getHeaderText = () => {
    if (selectedLocation) {
      return `Заявки на оплату - ${selectedLocation.location}`;
    }
    return "Заявки на оплату";
  };

  const smallButtons = [
    {
      id: "excel",
      label: "Экспорт в Excel",
      icon: <IconDownload />,
      variant: "primary" as const,
      onClick: handleExportPayment,
      disabled: paymentsLength === 0 && localPaymentsTable.total_count === 0,
    },
  ];

  return (
    <TabsLayout
      tabs={payment_tabs}
      header={getHeaderText()}
      subheaderText={`Количество заявок: ${localPaymentsTable.total_count}`}
      buttonText="Новая заявка"
      smallButtons={smallButtons}
    />
  );
}
